import { useMemo } from 'react'
import { convertDate } from '../../../helpers/dates-formatter'
import NoImage from '../../../assets/no-image.png'

export const useTournamentDisplay = tournament => {
  const registrationPeriod = useMemo(() => {
    if (
      !tournament?.registrationStartedAt ||
      !tournament?.registrationEndedAt
    ) {
      return null
    }
    return {
      startDate: convertDate(tournament.registrationStartedAt),
      endDate: convertDate(tournament.registrationEndedAt),
      displayText: `${convertDate(tournament.registrationStartedAt)} - ${convertDate(tournament.registrationEndedAt)}`,
    }
  }, [tournament?.registrationStartedAt, tournament?.registrationEndedAt])

  const tournamentDescription = useMemo(() => {
    return tournament?.details?.description || null
  }, [tournament?.details?.description])

  const platformData = useMemo(() => {
    if (!tournament?.details?.platform?.length) return null
    return {
      platforms: tournament.details.platform,
      hasPlatforms: true,
    }
  }, [tournament?.details?.platform])

  const organizerData = useMemo(() => {
    if (!tournament?.details?.organizerName) return null
    return {
      name: tournament.details.organizerName,
      logo: tournament?.media?.logo || NoImage,
      hasLogo: !!tournament?.media?.logo,
    }
  }, [tournament?.details?.organizerName, tournament?.media?.logo])

  const prizeData = useMemo(() => {
    return {
      amount: tournament?.details?.prizeFund || null,
      hasPrize: !!tournament?.details?.prizeFund,
    }
  }, [tournament?.details?.prizeFund])

  const gameData = useMemo(() => {
    if (!tournament?.game) return null
    return {
      logo: tournament.game.logo,
      title: tournament.game.title,
      size: tournament.size,
    }
  }, [tournament?.game, tournament?.size])

  const tournamentStats = useMemo(() => {
    return {
      participantLimit: tournament?.participantLimit,
      membersCount: tournament?.membersCount,
      systemType: tournament?.scheduleSettings?.systemType,
    }
  }, [
    tournament?.participantLimit,
    tournament?.membersCount,
    tournament?.scheduleSettings?.systemType,
  ])

  const contactsData = useMemo(() => {
    if (!tournament?.details?.contacts?.length) return null
    return {
      contacts: tournament.details.contacts,
      hasContacts: true,
    }
  }, [tournament?.details?.contacts])

  const socialNetworksData = useMemo(() => {
    if (!tournament?.details?.socialNetworks) return null
    return {
      discord: tournament.details.socialNetworks.discord,
      twitter: tournament.details.socialNetworks.twitter,
      facebook: tournament.details.socialNetworks.facebook,
      instagram: tournament.details.socialNetworks.instagram,
      hasAny: !!(
        tournament.details.socialNetworks.discord ||
        tournament.details.socialNetworks.twitter ||
        tournament.details.socialNetworks.facebook ||
        tournament.details.socialNetworks.instagram
      ),
    }
  }, [tournament?.details?.socialNetworks])

  const mediaData = useMemo(() => {
    const hasPartnerLogos = !!tournament?.media?.partnerLogo?.length
    const hasSponsorLogos = !!tournament?.media?.sponsorLogo?.length
    const hasOrganizerLogos = !!tournament?.media?.organizerLogo?.length

    return {
      partnerLogos: tournament?.media?.partnerLogo || [],
      sponsorLogos: tournament?.media?.sponsorLogo || [],
      organizerLogos: tournament?.media?.organizerLogo || [],
      hasAnyMedia: hasPartnerLogos || hasSponsorLogos || hasOrganizerLogos,
    }
  }, [tournament?.media])

  return {
    registrationPeriod,
    tournamentDescription,
    platformData,
    organizerData,
    prizeData,
    gameData,
    tournamentStats,
    contactsData,
    socialNetworksData,
    mediaData,
  }
}
